import { readTileProps, writeTileProps } from "./document.ts";
import { TILE_PROPS_SCHEMA_VERSION, type TileProps, type TilePropCell, type TilePropEntry, type TilePropSheets } from "./types.ts";

/**
 * The operations on a tile props document (ADR-102 decisions 4 and 24). Each returns a new document and leaves its
 * argument alone. An id comes from `next` and is never reused, an entry is never removed, and every write to an
 * entry raises that entry's `rev` by one and no other's. A candidate is checked by the reader, so the bounds of
 * decision 3 are one set of rules and a refusal names its path.
 */

/**
 * What a new entry is made of. The id and revision are the document's to give.
 */
export interface NewTileProp {
  readonly name: string;
  readonly group: string;
  readonly cols: number;
  readonly rows: number;
  readonly base?: number;
  readonly cells: readonly TilePropCell[];
}

/**
 * What an edit may change. The size is fixed once saved (ADR-102 decision 17), so it is not here; `base` of null
 * clears it, and absent leaves it.
 */
export interface TilePropChange {
  readonly name?: string;
  readonly group?: string;
  readonly base?: number | null;
  readonly cells?: readonly TilePropCell[];
}

/**
 * A document with no entries.
 *
 * @param pack The pack's id.
 * @returns The document, `next` 0.
 */
export function emptyTileProps(pack: string): TileProps {
  return { schemaVersion: TILE_PROPS_SCHEMA_VERSION, pack, next: 0, props: [] };
}

/**
 * Checks a candidate document by the reader and returns it normalised.
 *
 * @param candidate The document.
 * @param sheets The host's tilesets, if any.
 * @returns The document as the reader gives it.
 * @throws Error naming the path of the first fault.
 */
function checked(candidate: TileProps, sheets: TilePropSheets | undefined): TileProps {
  return readTileProps(writeTileProps(candidate), sheets);
}

/**
 * The entry with an id.
 *
 * @param document The document.
 * @param id The id.
 * @returns The entry.
 * @throws Error when there is none.
 */
function entryWith(document: TileProps, id: number): TilePropEntry {
  const entry = document.props.find((candidate) => candidate.id === id);
  if (entry === undefined) {
    throw new Error(`tileprops has no entry ${id}`);
  }
  return entry;
}

/**
 * The document with one entry replaced.
 *
 * @param document The document.
 * @param entry The replacement, of an id the document holds.
 * @returns The new document.
 */
function replaced(document: TileProps, entry: TilePropEntry): TileProps {
  return { ...document, props: document.props.map((candidate) => (candidate.id === entry.id ? entry : candidate)) };
}

/**
 * Refuses a name another entry holds, ignoring case, at the path of the entry being written so the message points
 * at the edit and not at the older entry the reader would name.
 *
 * @param document The document without the entry being written.
 * @param name The entry's name.
 * @param index The entry's place in the written document.
 * @throws Error naming `tileprops.props[<index>].name` when another entry holds the name.
 */
function assertNameFree(document: TileProps, name: string, index: number): void {
  const key = name.toLowerCase();
  const holder = document.props.find((entry) => entry.name.toLowerCase() === key);
  if (holder !== undefined) {
    throw new Error(`tileprops.props[${index}].name ${JSON.stringify(name)} is already the name of entry ${holder.id}, names being unique in a pack ignoring case`);
  }
}

/**
 * What an entry draws and is called, without its revision: two entries with equal keys are one write's worth the
 * same.
 *
 * @param entry The entry.
 * @returns A string equal for equal content.
 */
function contentKey(entry: TilePropEntry): string {
  const cells = entry.cells.map((cell) => [cell.x, cell.y, cell.tile.tileset, cell.tile.tile, cell.tile.flipX === true, cell.tile.flipY === true]);
  return JSON.stringify([entry.name, entry.group, entry.cols, entry.rows, entry.base ?? null, entry.retired === true, cells]);
}

/**
 * Adds an entry under the next id, at revision 1, and raises `next`.
 *
 * @param document The document.
 * @param input The entry's name, group, size, optional base and cells.
 * @param sheets The host's tilesets, if any (see `readTileProps`).
 * @returns The new document and the id the entry took.
 * @throws Error naming the path of the first fault the entry would have in the document.
 */
export function addTileProp(document: TileProps, input: NewTileProp, sheets?: TilePropSheets): { readonly document: TileProps; readonly id: number } {
  const id = document.next;
  const entry: TilePropEntry = { id, rev: 1, name: input.name, group: input.group, cols: input.cols, rows: input.rows, ...(input.base === undefined ? {} : { base: input.base }), cells: input.cells };
  const next = checked({ ...document, next: id + 1, props: [...document.props, entry] }, sheets);
  return { document: { ...next, props: [...document.props, next.props[next.props.length - 1] as TilePropEntry] }, id };
}

/**
 * Edits an entry: its name, group, base or cells, never its size. It raises the entry's `rev`; a change that
 * changes nothing is no write and returns the document itself.
 *
 * @param document The document.
 * @param id The entry's id.
 * @param change The fields to set.
 * @param sheets The host's tilesets, if any.
 * @returns The new document.
 * @throws Error when there is no such entry, or naming the path of the first fault the edited entry would have.
 */
export function editTileProp(document: TileProps, id: number, change: TilePropChange, sheets?: TilePropSheets): TileProps {
  const old = entryWith(document, id);
  const { base: _base, ...rest } = old;
  const base = change.base === undefined ? old.base : change.base ?? undefined;
  const entry: TilePropEntry = { ...rest, rev: old.rev + 1, name: change.name ?? old.name, group: change.group ?? old.group, cells: change.cells ?? old.cells, ...(base === undefined ? {} : { base }) };
  assertNameFree({ ...document, props: document.props.filter((other) => other.id !== id) }, entry.name, document.props.indexOf(old));
  const read = checked(replaced(document, entry), sheets);
  const normalised = entryWith(read, id);
  return contentKey(normalised) === contentKey(old) ? document : replaced(document, normalised);
}

/**
 * Retires an entry: it leaves the palette and keeps its cells, so a placement of it still draws (ADR-102 decision
 * 24). The write raises the entry's `rev`; an entry already retired is no write.
 *
 * @param document The document.
 * @param id The entry's id.
 * @returns The new document.
 * @throws Error when there is no such entry.
 */
export function retireTileProp(document: TileProps, id: number): TileProps {
  const old = entryWith(document, id);
  return old.retired === true ? document : replaced(document, { ...old, rev: old.rev + 1, retired: true });
}

/**
 * Restores a retired entry to the palette, its cells as they were. The write raises the entry's `rev`; an entry
 * that is not retired is no write.
 *
 * @param document The document.
 * @param id The entry's id.
 * @returns The new document.
 * @throws Error when there is no such entry.
 */
export function restoreTileProp(document: TileProps, id: number): TileProps {
  const old = entryWith(document, id);
  if (old.retired !== true) {
    return document;
  }
  const { retired: _retired, ...rest } = old;
  return replaced(document, { ...rest, rev: old.rev + 1 });
}
